import React, {useState} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {Field} from 'formik';
import markFilledInput from '../../assets/markFilledInput.svg';
import eye from '../../assets/visibility-24px.svg';
import percent from '../../assets/percentage.svg';
import styles from './styles.module.scss';

const TYPES = {
  TEXT: 'text',
  NUMBER: 'number',
  EMAIL: 'email',
  PASSWORD: 'password',
  TEL: 'tel'
};

const FieldInput = ({
                      fieldId,
                      type = 'text',
                      label = null,
                      mandatoryMark = false,
                      showBird = true,
                      onChange,
                      readonly,
                      containerCustomStyles = null, // deprecated, legacy usage only
                      classNames: classNamesProp = {},
                      peepPassword = false,
                      showPercentIcon = false,
                      ltrDirection = false,
                      ...rest
                    }) => {
  const classNames = {
    container: containerCustomStyles,
    ...classNamesProp
  };
  const [passwordShown, setPasswordShown] = useState(false);
  const togglePasswordVisibility = () => {
    setPasswordShown(prevState => !prevState);
  };

  const getPasswordInputType = () => {
    return passwordShown ? 'text' : 'password';
  };

  const getInputType = () => {
    return type === TYPES.PASSWORD ? getPasswordInputType() : type;
  };

  const handleInputChange = ({form, field, meta}) => e => {
    const value = e.target.value;
    if (onChange) {
      const changedValue = onChange(e);
      form.setFieldValue(field.name, changedValue);
      form.setFieldTouched(fieldId, true);
    } else {
      form.setFieldValue(field.name, value);
      form.setFieldTouched(fieldId, true);
    }
  };

  const showErrorLabel = meta => {
    if (Boolean(readonly)) {
      return false;
    } else {
      return meta.error && meta.touched;
    }
  };

  const showBirdMark = meta => {
    if (Boolean(readonly)) {
      return false;
    } else {
      return !meta.error && meta.touched && meta.value && showBird;
    }
  };

  const checkValueTypeIsNumber = field => {
    const parsedValue = parseFloat(field.value);
    const isNumberStr = (typeof parsedValue === 'number') && !isNaN(parsedValue);
    const isNumber = typeof field.value === 'number';

    return isNumber || isNumberStr;
  };

  return (
    <Field
      id={fieldId}
      name={fieldId}
    >
      {({form, field, meta}) => (
        <label
          className={classnames(styles.inputContainer, classNames.container)}>
          {label && (
            <div
              className={classnames(styles.labelContainer, classNames.label, {[styles.errorLabel]: showErrorLabel(meta)})}
            >
              {mandatoryMark && <span className={styles.mandatoryMark}>*</span>}
              {label}
            </div>
          )}
          <div className={styles.inputBox}>
            <div
              className={classnames(styles.markFilledInputOff, {[styles.markFilledInputOn]: showBirdMark(meta)})}
            >
              <img
                src={markFilledInput}
                alt="markFilledInput"
              />
            </div>
            {showPercentIcon && checkValueTypeIsNumber(field) &&
            <img
              src={percent}
              alt="percent"
              className={styles.percentIcon}
            />
            }
            {(type === 'password') && peepPassword && (
              <div
                className={styles.iconEyeContainer}
                onClick={togglePasswordVisibility}
              >
                <img
                  src={eye}
                  alt="peep"
                  className={styles.iconEye}
                />
              </div>
            )}
            <input
              className={classnames(styles.input, classNames.input,
                {[styles.errorBorder]: meta.error && meta.touched},
                {[styles.percentIconInput]: showPercentIcon},
                {[styles.ltrDirection]: ltrDirection})
              }
              type={getInputType()}
              onChange={handleInputChange({form, field, meta})}
              value={field.value}
              readOnly={readonly}
              {...rest}
            />
          </div>
          <div
            className={classnames(styles.errorMessageOff, {[styles.errorMessageOn]: meta.error && meta.touched})}
          >
            <p className={styles.errorText}>{meta.error}</p>
          </div>
        </label>
      )}
    </Field>
  );
};

FieldInput.propTypes = {
  fieldId: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  type: PropTypes.oneOf(Object.values(TYPES)),
  label: PropTypes.node,
  mandatoryMark: PropTypes.bool,
  showBird: PropTypes.bool,
  peepPassword: PropTypes.bool,
  labelStyles: PropTypes.object,
  onChange: PropTypes.func,
  classNames: PropTypes.shape({
    input: PropTypes.string,
    label: PropTypes.string,
    container: PropTypes.string,
  })
};

export default FieldInput;
